import { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone, Mail, MapPin, Sparkles, ChevronRight } from "lucide-react";
import { BRAND } from "@/config/brand";
import { DFW_CITIES, CITY_FAQS } from "@/config/cities";
import PlugitAIChat from "@/pages/PlugitAIChat";
import AITwinSlideshow from "@/pages/AITwinSlideshow";
import FounderLetter from "@/pages/FounderLetter";

const C = BRAND.colors;

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function upsertMeta(nameOrProp, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${nameOrProp}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, nameOrProp);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function CityPage() {
  const { city: slug } = useParams();
  const city = useMemo(() => DFW_CITIES.find((c) => c.slug === slug), [slug]);

  useEffect(() => {
    if (!city) return;
    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    const url = `${origin}/tx/${city.slug}`;
    const title = `AI Website Design in ${city.name}, TX — $299.99 to Lock In | PlugitAI`;
    const desc = `AI website design and AI sales agents for ${city.name}, TX small businesses. Websites that answer leads, book appointments, send forms, and take payments 24/7. Projects start with a $299.99 lock-in.`;
    document.title = title;
    upsertMeta("description", desc);
    upsertMeta("robots", "index,follow,max-image-preview:large,max-snippet:-1");
    upsertMeta("og:title", title, true);
    upsertMeta("og:description", desc, true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:url", url, true);
    upsertMeta("og:image", `${origin}/hero-ai-twin.jpg`, true);
    upsertMeta("twitter:card", "summary_large_image");
    upsertCanonical(url);

    // LocalBusiness schema
    upsertJsonLd("ld-localbusiness", {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: `PlugitAI — ${city.name}`,
      image: `${origin}/plugitai-logo.svg`,
      url,
      telephone: "+12148155966",
      email: "cgarciathanksyou@gmail.com",
      priceRange: "$299.99+",
      areaServed: [{ "@type": "City", name: city.name }, ...city.nearby.map((n) => ({ "@type": "City", name: n.replace(/-/g, " ") }))],
      address: {
        "@type": "PostalAddress",
        addressLocality: city.name,
        addressRegion: "TX",
        postalCode: city.zip,
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: city.lat, longitude: city.lng },
      openingHours: "Mo-Su 00:00-23:59",
    });

    // Breadcrumb schema
    upsertJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
        { "@type": "ListItem", position: 2, name: "Texas", item: `${origin}/tx` },
        { "@type": "ListItem", position: 3, name: city.name, item: url },
      ],
    });

    // FAQ schema
    upsertJsonLd("ld-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: CITY_FAQS(city).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    return () => {
      ["ld-localbusiness", "ld-breadcrumb", "ld-faq"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [city]);

  if (!city) return <Navigate to="/" replace />;

  const faqs = CITY_FAQS(city);

  return (
    <div className="min-h-screen w-full overflow-x-hidden"
         style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid={`city-page-${city.slug}`}>
      {/* Ambient background — subtle */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0"
           style={{
             background: `radial-gradient(1200px 700px at 15% -10%, ${C.blueBright}22, transparent 60%),
                          radial-gradient(900px 600px at 90% 15%, ${C.greenBright}18, transparent 60%),
                          radial-gradient(1400px 900px at 60% 110%, ${C.blueDeep}44, transparent 70%)`,
           }} />

      <TopNav city={city} />

      {/* Local Hero */}
      <section className="relative z-10 pt-28 md:pt-36 pb-16 md:pb-24" data-testid="city-hero">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <Breadcrumbs city={city} />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-10 lg:gap-14 items-center mt-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-[0.2em] mb-8"
                   style={{
                     background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                     color: "white",
                     boxShadow: `0 12px 32px -10px ${C.blueBright}88`,
                   }}>
                <MapPin size={12} />
                Serving {city.name}, TX · {city.county}
              </div>

              <h1 className="font-black tracking-[-0.02em] leading-[0.98]"
                  style={{ fontSize: "clamp(2.4rem, 5vw, 5rem)" }}
                  data-testid="city-h1">
                AI website design in{" "}
                <span style={{
                  backgroundImage: `linear-gradient(135deg, ${C.blueGlow} 0%, ${C.greenGlow} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  filter: `drop-shadow(0 0 20px ${C.blueBright}55)`,
                }}>{city.name}, TX</span>
                .
              </h1>

              <p className="mt-6 max-w-[620px] text-lg md:text-xl leading-relaxed" style={{ color: C.textDim }}>
                {city.hook}
              </p>
              <p className="mt-4 max-w-[620px] text-base md:text-lg leading-relaxed" style={{ color: C.textDim }}>
                We build websites and{" "}
                <span className="text-white font-semibold">AI sales agents</span> for{" "}
                {city.industries.slice(0, 4).join(", ")} and other {city.name} businesses. Every
                site answers visitors, books appointments, sends forms, and takes approved payments —{" "}
                <span className="text-white font-semibold">24/7</span>.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a href="tel:+12148155966"
                   className="inline-flex items-center gap-2 px-6 md:px-8 h-14 rounded-full font-semibold text-base transition-all hover:-translate-y-0.5"
                   style={{
                     background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                     color: "white",
                     boxShadow: `0 20px 60px -20px ${C.greenBright}88`,
                   }}
                   data-testid="city-cta-call">
                  Call (214) 815-5966 <ArrowRight size={16} />
                </a>
                <a href={`mailto:cgarciathanksyou@gmail.com?subject=Website%20for%20${encodeURIComponent(city.name)}`}
                   className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border transition-all hover:bg-white/5"
                   style={{ borderColor: `${C.blueBright}66`, color: C.text }}
                   data-testid="city-cta-email">
                  Email me <ArrowUpRight size={16} />
                </a>
              </div>
            </div>

            <div className="order-first lg:order-last">
              <AITwinSlideshow />
            </div>
          </div>
        </div>
      </section>

      {/* Industries served in this city */}
      <section className="relative z-10 py-16 md:py-20 border-y" style={{ borderColor: C.line, background: "rgba(17,24,39,0.55)" }}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.greenGlow }}>
            Industries we build for in {city.name}
          </div>
          <div className="flex flex-wrap gap-2">
            {city.industries.map((ind) => (
              <span key={ind}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
                    style={{
                      background: "rgba(59,130,246,0.10)",
                      borderColor: `${C.blueBright}55`,
                      color: C.blueGlow,
                    }}>
                <Sparkles size={12} /> {ind}
              </span>
            ))}
          </div>
          {city.nearby.length > 0 && (
            <div className="mt-6 text-sm" style={{ color: C.textDim }}>
              <span className="uppercase tracking-[0.18em] text-xs mr-3" style={{ color: C.textFade }}>Also serving:</span>
              {city.nearby.map((n, i) => (
                <span key={n}>
                  <Link to={`/tx/${n}`} className="underline underline-offset-2 hover:text-white transition-colors">
                    {n.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")}
                  </Link>
                  {i < city.nearby.length - 1 && <span className="mx-2" style={{ color: C.textFade }}>·</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Full founder letter (shared with home) */}
      <FounderLetter />

      {/* FAQ */}
      <section className="relative z-10 py-24 md:py-32" data-testid="city-faq">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.greenGlow }}>
            Questions from {city.name} owners
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-10"
              style={{ fontSize: "clamp(1.9rem, 3.6vw, 3rem)" }}>
            FAQ
          </h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details key={i}
                       className="group rounded-2xl p-5 md:p-6 border"
                       style={{ borderColor: C.line, background: "rgba(17,24,39,0.55)", backdropFilter: "blur(10px)" }}
                       data-testid={`city-faq-${i}`}>
                <summary className="flex items-center justify-between gap-4 cursor-pointer text-base md:text-lg font-bold text-white list-none">
                  <span>{f.q}</span>
                  <ChevronRight size={18} className="shrink-0 transition-transform group-open:rotate-90" style={{ color: C.blueGlow }} />
                </summary>
                <p className="mt-3 text-sm md:text-base leading-relaxed" style={{ color: C.textDim }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CityFooter city={city} />
      <PlugitAIChat />
    </div>
  );
}

function TopNav({ city }) {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px) saturate(140%)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto drop-shadow-[0_0_18px_rgba(59,130,246,0.35)]" />
          <span className="hidden sm:inline-block text-[10px] md:text-[11px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-3 text-xs uppercase tracking-[0.2em]" style={{ color: C.textFade }}>
          <MapPin size={12} style={{ color: C.greenGlow }} />
          <span>{city.name}, TX</span>
        </div>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}

function Breadcrumbs({ city }) {
  return (
    <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 flex-wrap" style={{ color: C.textFade }} data-testid="city-breadcrumbs">
      <Link to="/" className="hover:text-white transition-colors">Home</Link>
      <ChevronRight size={12} />
      <Link to="/tx" className="hover:text-white transition-colors">Texas</Link>
      <ChevronRight size={12} />
      <span style={{ color: C.text }}>{city.name}</span>
    </nav>
  );
}

function CityFooter({ city }) {
  return (
    <footer className="relative z-10 border-t" style={{ borderColor: C.line, background: "rgba(10,15,30,0.85)" }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-11 w-auto mb-4" />
          <p className="text-sm max-w-[440px]" style={{ color: C.textDim }}>
            {BRAND.platformName} builds AI-powered websites for small businesses in {city.name}, TX
            and across the DFW metroplex. <span className="gold-shine gold-shine-dark uppercase tracking-[0.22em] text-[10px]">Powered by {BRAND.engineName}</span>.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.textFade }}>Serving</div>
          <ul className="space-y-2 text-sm">
            {city.nearby.slice(0, 5).map((n) => (
              <li key={n}>
                <Link to={`/tx/${n}`} className="hover:text-white" style={{ color: C.textDim }}>
                  {n.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")}, TX
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.textFade }}>Contact</div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2" style={{ color: C.textDim }}>
              <Phone size={12} /> <a href="tel:+12148155966" className="hover:text-white">(214) 815-5966</a>
            </li>
            <li className="flex items-center gap-2" style={{ color: C.textDim }}>
              <Mail size={12} /> <a href="mailto:cgarciathanksyou@gmail.com" className="hover:text-white">Email Me</a>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
